import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addDays,
  format,
  isSameMonth,
  parseISO,
} from 'date-fns'

/**
 * Creates a 42-day grid for a month view calendar (6 weeks × 7 days)
 * Includes leading days from previous month and trailing days from next month
 *
 * @param {Date|string} date - Date object or ISO string representing any day in the target month
 * @param {Object} options - Configuration options
 * @param {number} options.weekStartsOn - Day of week to start on (0 = Sunday, 1 = Monday, etc.) (default: 0)
 * @returns {Array<Object>} Array of 42 day objects with structure:
 *   - date: Date object for the day
 *   - dateString: ISO date string (YYYY-MM-DD)
 *   - isCurrentMonth: boolean indicating if day belongs to target month
 *   - isLeading: boolean indicating if day is from previous month
 *   - isTrailing: boolean indicating if day is from next month
 *
 * @example
 * createMonthGrid(new Date('2025-01-15'))
 * // Returns 42 days from Dec 29, 2024 to Feb 8, 2025 (Sunday start)
 *
 * createMonthGrid(new Date('2025-01-15'), { weekStartsOn: 1 })
 * // Returns 42 days from Dec 30, 2024 to Feb 9, 2025 (Monday start)
 */
export function createMonthGrid(date, options = {}) {
  const { weekStartsOn = 0 } = options
  const targetDate = typeof date === 'string' ? parseISO(date) : date

  const monthStart = startOfMonth(targetDate)
  const monthEnd = endOfMonth(targetDate)

  const gridStart = startOfWeek(monthStart, { weekStartsOn })
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn })

  const days = eachDayOfInterval({ start: gridStart, end: gridEnd })

  // Ensure we always have exactly 42 days (6 weeks)
  // If we have fewer than 42 days, add additional days from the next week
  const totalDays = days.length
  if (totalDays < 42) {
    const lastDay = days[totalDays - 1]
    for (let i = 1; i <= 42 - totalDays; i++) {
      days.push(addDays(lastDay, i))
    }
  }

  return days.map((day) => {
    const isCurrentMonth = isSameMonth(day, targetDate)
    return {
      date: day,
      dateString: format(day, 'yyyy-MM-dd'),
      isCurrentMonth,
      isLeading: !isCurrentMonth && day < monthStart,
      isTrailing: !isCurrentMonth && day > monthEnd,
    }
  })
}

/**
 * Generic helper function to group items by date key
 * Can be used with any array of objects containing date properties
 *
 * **Important:** Date grouping uses the local timezone when formatting dates to YYYY-MM-DD keys.
 * This means the same UTC timestamp may be grouped under different dates for users in different timezones.
 * For example, '2025-01-15T23:30:00Z' groups as '2025-01-15' in UTC but '2025-01-16' in UTC+1 or later.
 *
 * @param {Array<Object>} items - Array of items to group
 * @param {string} dateProperty - Property name containing the date to group by
 * @returns {Object} Object mapping date strings (YYYY-MM-DD) to arrays of items
 *
 * @example
 * groupByDateProperty([
 *   { created_at: '2025-01-15T10:00:00Z', name: 'Item 1' },
 *   { created_at: '2025-01-15T14:00:00Z', name: 'Item 2' },
 * ], 'created_at')
 * // Returns { '2025-01-15': [{ created_at: ..., name: 'Item 1' }, { created_at: ..., name: 'Item 2' }] }
 */
export function groupByDateProperty(items, dateProperty) {
  if (!Array.isArray(items)) {
    return {}
  }

  return items.reduce((acc, item) => {
    if (!item[dateProperty]) {
      return acc
    }

    try {
      const date =
        typeof item[dateProperty] === 'string'
          ? parseISO(item[dateProperty])
          : item[dateProperty]

      // Validate that we have a valid Date object
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return acc
      }

      const dateKey = format(date, 'yyyy-MM-dd')

      if (!acc[dateKey]) {
        acc[dateKey] = []
      }
      acc[dateKey].push(item)
    } catch {
      // Skip items with invalid dates
      return acc
    }

    return acc
  }, {})
}

/**
 * Groups availability records by date key
 *
 * @param {Array<Object>} availability - Array of availability objects with start_time property
 * @returns {Object} Object mapping date strings (YYYY-MM-DD) to arrays of availability items
 *
 * @example
 * groupAvailabilityByDate([
 *   { start_time: '2025-01-15T10:00:00Z', end_time: '2025-01-15T12:00:00Z' },
 *   { start_time: '2025-01-15T14:00:00Z', end_time: '2025-01-15T16:00:00Z' },
 * ])
 * // Returns { '2025-01-15': [{ start_time: ..., end_time: ... }, { start_time: ..., end_time: ... }] }
 */
export function groupAvailabilityByDate(availability) {
  return groupByDateProperty(availability, 'start_time')
}

/**
 * Groups opponent availability records by date key
 * Same as groupAvailabilityByDate but with a more descriptive name for clarity
 *
 * @param {Array<Object>} availability - Array of opponent availability objects with start_time property
 * @returns {Object} Object mapping date strings (YYYY-MM-DD) to arrays of opponent availability items
 *
 * @example
 * groupOpponentAvailabilityByDate([
 *   { start_time: '2025-01-15T10:00:00Z', end_time: '2025-01-15T12:00:00Z', opponent_user_id: 1 },
 *   { start_time: '2025-01-15T14:00:00Z', end_time: '2025-01-15T16:00:00Z', opponent_user_id: 2 },
 * ])
 * // Returns { '2025-01-15': [{ start_time: ..., end_time: ..., opponent_user_id: 1 }, { start_time: ..., end_time: ..., opponent_user_id: 2 }] }
 */
export function groupOpponentAvailabilityByDate(availability) {
  return groupByDateProperty(availability, 'start_time')
}

/**
 * Groups match proposal records by date key
 *
 * @param {Array<Object>} proposals - Array of proposal objects with proposed_time property
 * @returns {Object} Object mapping date strings (YYYY-MM-DD) to arrays of proposal items
 *
 * @example
 * groupProposalsByDate([
 *   { proposed_time: '2025-01-20T15:00:00Z', status: 'pending' },
 *   { proposed_time: '2025-01-20T18:00:00Z', status: 'accepted' },
 * ])
 * // Returns { '2025-01-20': [{ proposed_time: ..., status: ... }, { proposed_time: ..., status: ... }] }
 */
export function groupProposalsByDate(proposals) {
  return groupByDateProperty(proposals, 'proposed_time')
}

/**
 * Groups match records by date key
 * Filters out unscheduled matches (status='created') as they should not appear on the calendar
 *
 * @param {Array<Object>} matches - Array of match objects with match_datetime property
 * @returns {Object} Object mapping date strings (YYYY-MM-DD) to arrays of match items
 *
 * @example
 * groupMatchesByDate([
 *   { match_datetime: '2025-01-25T10:00:00Z', status: 'scheduled' },
 *   { match_datetime: '2025-01-25T14:00:00Z', status: 'created' }, // Filtered out
 * ])
 * // Returns { '2025-01-25': [{ match_datetime: ..., status: 'scheduled' }] }
 */
export function groupMatchesByDate(matches) {
  if (!Array.isArray(matches)) {
    return {}
  }

  // Only show scheduled and finalized matches on the calendar.
  // Use an allow-list so unknown/unscheduled statuses don't show up.
  const calendarStatuses = new Set([
    'scheduled',
    'normal',
    'home_retired',
    'away_retired',
    'i_retired',
    'opponent_retired',
  ])
  const calendarMatches = matches.filter((match) =>
    calendarStatuses.has(match.status),
  )

  return groupByDateProperty(calendarMatches, 'match_datetime')
}

/**
 * Gets all items for a specific day from grouped data
 *
 * @param {Date|string} date - Date object or ISO string for the target day
 * @param {Object} data - Object with date keys mapping to arrays of items (from grouping functions)
 * @returns {Array} Array of items for the specified date, or empty array if none
 *
 * @example
 * const groupedData = { '2025-01-15': [item1, item2], '2025-01-16': [item3] }
 * getItemsForDay('2025-01-15', groupedData)
 * // Returns [item1, item2]
 */
export function getItemsForDay(date, data) {
  if (!data || typeof data !== 'object') {
    return []
  }

  try {
    const targetDate = typeof date === 'string' ? parseISO(date) : date

    // Validate that we have a valid Date object
    if (!(targetDate instanceof Date) || isNaN(targetDate.getTime())) {
      return []
    }

    const dateKey = format(targetDate, 'yyyy-MM-dd')
    return data[dateKey] || []
  } catch {
    // Return empty array for invalid dates
    return []
  }
}

/**
 * Gets all calendar items (availability, proposals, matches) for a specific day
 * More efficient than calling getItemsForDay three times as it only formats the date once
 *
 * @param {Date|string} date - Date object or ISO string for the target day
 * @param {Object} availabilityByDate - Object with date keys mapping to availability arrays
 * @param {Object} proposalsByDate - Object with date keys mapping to proposal arrays
 * @param {Object} matchesByDate - Object with date keys mapping to match arrays
 * @param {Object} [opponentAvailabilityByDate] - Optional object with date keys mapping to opponent availability arrays
 * @returns {Object} Object with structure:
 *   - availability: Array of availability items for the date
 *   - opponentAvailability: Array of opponent availability items for the date (if provided)
 *   - proposals: Array of proposal items for the date
 *   - matches: Array of match items for the date
 *
 * @example
 * const result = getAllItemsForDay(
 *   new Date('2025-01-15'),
 *   availabilityByDate,
 *   proposalsByDate,
 *   matchesByDate,
 *   opponentAvailabilityByDate
 * )
 * // Returns { availability: [...], opponentAvailability: [...], proposals: [...], matches: [...] }
 */
export function getAllItemsForDay(
  date,
  availabilityByDate,
  proposalsByDate,
  matchesByDate,
  opponentAvailabilityByDate,
) {
  try {
    const targetDate = typeof date === 'string' ? parseISO(date) : date

    // Validate that we have a valid Date object
    if (!(targetDate instanceof Date) || isNaN(targetDate.getTime())) {
      return {
        availability: [],
        opponentAvailability: [],
        proposals: [],
        matches: [],
      }
    }

    const dateKey = format(targetDate, 'yyyy-MM-dd')

    return {
      availability:
        availabilityByDate && typeof availabilityByDate === 'object'
          ? availabilityByDate[dateKey] || []
          : [],
      opponentAvailability:
        opponentAvailabilityByDate &&
        typeof opponentAvailabilityByDate === 'object'
          ? opponentAvailabilityByDate[dateKey] || []
          : [],
      proposals:
        proposalsByDate && typeof proposalsByDate === 'object'
          ? proposalsByDate[dateKey] || []
          : [],
      matches:
        matchesByDate && typeof matchesByDate === 'object'
          ? matchesByDate[dateKey] || []
          : [],
    }
  } catch {
    // Return empty arrays for invalid dates
    return {
      availability: [],
      opponentAvailability: [],
      proposals: [],
      matches: [],
    }
  }
}

/**
 * Generates colored dot indicators for calendar day display
 * Limits to maximum of 3 dots with "+ N more" logic for overflow
 *
 * @param {Array} items - Array of items to generate indicators for
 * @param {Object} options - Configuration options
 * @param {number} options.maxDots - Maximum number of dots to display (default: 3)
 * @param {Function} options.getColor - Function to determine color for each item (default: returns 'blue')
 * @returns {Object} Object with structure:
 *   - dots: Array of color strings for display
 *   - overflowCount: Number of additional items beyond maxDots
 *   - hasOverflow: Boolean indicating if there are more items than maxDots
 *   - totalCount: Total number of items
 *
 * @example
 * getDotIndicators([item1, item2, item3, item4])
 * // Returns { dots: ['blue', 'blue', 'blue'], overflowCount: 1, hasOverflow: true, totalCount: 4 }
 *
 * getDotIndicators([item1, item2], {
 *   maxDots: 3,
 *   getColor: (item) => item.status === 'pending' ? 'yellow' : 'green'
 * })
 * // Returns { dots: ['yellow', 'green'], overflowCount: 0, hasOverflow: false, totalCount: 2 }
 */
export function getDotIndicators(items, options = {}) {
  const { maxDots = 3, getColor = () => 'blue' } = options

  if (!Array.isArray(items) || items.length === 0) {
    return {
      dots: [],
      overflowCount: 0,
      hasOverflow: false,
      totalCount: 0,
    }
  }

  // Validate getColor is a function
  const colorFunction = typeof getColor === 'function' ? getColor : () => 'blue'

  const visibleItems = items.slice(0, maxDots)
  const dots = visibleItems.map((item) => {
    try {
      return colorFunction(item)
    } catch (error) {
      // If getColor throws an error, fall back to default blue
      console.warn('Error in getColor callback, using default color:', error)
      return 'blue'
    }
  })
  const overflowCount = Math.max(0, items.length - maxDots)

  return {
    dots,
    overflowCount,
    hasOverflow: overflowCount > 0,
    totalCount: items.length,
  }
}
